// src/pages/Performance.js
import React, { useState, useEffect } from 'react';
import { getPortfolioHistory, getOrders, STRATEGY_NAMES, getSymbolStrategy } from '../services/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import './Performance.css';

const Performance = () => {
  const [selectedStrategy, setSelectedStrategy] = useState('all');
  const [history, setHistory] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [historyData, ordersData] = await Promise.all([
          getPortfolioHistory('all'),
          getOrders('closed', 500)
        ]);

        // Transform history
        const chartData = historyData.timestamp.map((t, i) => ({
          date: new Date(t * 1000).toLocaleDateString(),
          equity: historyData.equity[i],
          pnl: i > 0 ? historyData.equity[i] - historyData.equity[i - 1] : 0,
          pnlPct: i > 0 && historyData.equity[i - 1] > 0
            ? ((historyData.equity[i] - historyData.equity[i - 1]) / historyData.equity[i - 1]) * 100
            : 0,
          fill: (i > 0 && historyData.equity[i] >= historyData.equity[i - 1]) ? '#00ff88' : '#ff4757'
        }));

        setHistory(chartData);

        // Trades to exclude due to data errors (e.g. strategy not yet live)
        const EXCLUDED_TRADES = [
          { symbol: 'DE', strategy: 'momentum', date: '2026-03-25' }
        ];

        const isExcluded = (symbol, strategy, exitTime) => {
          return EXCLUDED_TRADES.some(e =>
            e.symbol === symbol &&
            e.strategy === strategy &&
            new Date(exitTime).toISOString().startsWith(e.date)
          );
        };

        // Group orders into completed trades, handling both long (buy→sell) and short (sell→buy).
        const filledOrders = ordersData.orders
          .filter(o => o.status === 'filled' && o.filled_at)
          .sort((a, b) => new Date(a.filled_at) - new Date(b.filled_at));

        const completedTrades = [];
        const openPositions = {}; // symbol -> opening order

        filledOrders.forEach(order => {
          const symbol = order.symbol;
          const open = openPositions[symbol];

          if (!open) {
            openPositions[symbol] = order;
          } else if (open.side !== order.side) {
            const sellOrder = open.side === 'sell' ? open : order;
            const buyOrder  = open.side === 'buy'  ? open : order;

            const strategy = getSymbolStrategy(symbol);
            if (!isExcluded(symbol, strategy, order.filled_at)) {
              const pnl = (sellOrder.filled_avg_price - buyOrder.filled_avg_price) * order.filled_qty;
              const pnlPercent = ((sellOrder.filled_avg_price - buyOrder.filled_avg_price) / buyOrder.filled_avg_price) * 100;

              completedTrades.push({
                symbol,
                strategy,
                side: open.side === 'buy' ? 'long' : 'short',
                pnl,
                pnlPercent,
                exit_time: order.filled_at
              });
            }
            delete openPositions[symbol];
          } else {
            openPositions[symbol] = order;
          }
        });

        setTrades(completedTrades);
        setLoading(false);
      } catch (error) {
        console.error('Error loading performance data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return <div className="loading">Loading performance data...</div>;
  }

  // Filter trades by selected strategy
  const filteredTrades = selectedStrategy === 'all' 
    ? trades 
    : trades.filter(t => t.strategy === selectedStrategy);

  // Calculate metrics
  const winners = filteredTrades.filter(t => t.pnl > 0);
  const losers = filteredTrades.filter(t => t.pnl <= 0);
  const winRate = filteredTrades.length > 0 ? (winners.length / filteredTrades.length) * 100 : 0;
  const avgWin = winners.length > 0 ? winners.reduce((sum, t) => sum + t.pnlPercent, 0) / winners.length : 0;
  const avgLoss = losers.length > 0 ? losers.reduce((sum, t) => sum + t.pnlPercent, 0) / losers.length : 0;
  
  const profitFactor = losers.length > 0
    ? Math.abs(winners.reduce((sum, t) => sum + t.pnl, 0) / losers.reduce((sum, t) => sum + t.pnl, 0))
    : 0;

  const totalPnL = filteredTrades.reduce((sum, t) => sum + t.pnl, 0);

  // Strategy colors
  const strategyColors = {
    vwap: '#00d4ff',
    meanrev: '#00ff88',
    momentum: '#ffd93d'
  };

  return (
    <div className="performance">
      <div className="performance-header">
        <h1>Performance Analytics</h1>
        <div className="controls-row">
          {/* Strategy Selector */}
          <div className="strategy-selector">
            <label>Strategy:</label>
            <select
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
              className="strategy-dropdown"
            >
              <option value="all">{STRATEGY_NAMES.all}</option>
              <option value="vwap">{STRATEGY_NAMES.vwap}</option>
              <option value="meanrev">{STRATEGY_NAMES.meanrev}</option>
              <option value="momentum">{STRATEGY_NAMES.momentum}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total Trades</div>
          <div className="metric-value">{filteredTrades.length}</div>
          {selectedStrategy !== 'all' && (
            <div className="metric-subtext">
              <span style={{color: strategyColors[selectedStrategy]}}>
                {STRATEGY_NAMES[selectedStrategy]}
              </span>
            </div>
          )}
        </div>
        <div className="metric-card">
          <div className="metric-label">Win Rate</div>
          <div className={`metric-value ${winRate >= 50 ? 'positive' : 'negative'}`}>
            {winRate.toFixed(1)}%
          </div>
          <div className="metric-subtext">{winners.length}W / {losers.length}L</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total P&L</div>
          <div className={`metric-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
            {totalPnL >= 0 ? '+' : ''}${totalPnL.toFixed(2)}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Profit Factor</div>
          <div className={`metric-value ${profitFactor >= 1.5 ? 'positive' : 'negative'}`}>
            {profitFactor.toFixed(2)}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Avg Win</div>
          <div className="metric-value positive">+{avgWin.toFixed(2)}%</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Avg Loss</div>
          <div className="metric-value negative">{avgLoss.toFixed(2)}%</div>
        </div>
      </div>

      {/* Equity Curve */}
      <div className="chart-container">
        <h2>Equity Curve</h2>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={history}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
            <XAxis dataKey="date" stroke="#8b92a7" />
            <YAxis stroke="#8b92a7" />
            <Tooltip 
              contentStyle={{ background: '#0f1729', border: '1px solid #1a2332', borderRadius: '8px' }}
              labelStyle={{ color: '#8b92a7' }}
            />
            <Line type="monotone" dataKey="equity" stroke="#00d4ff" strokeWidth={3} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Daily P&L */}
      <div className="chart-container">
        <h2>Daily P&L (%)</h2>
        {history.length === 0 ? (
          <div className="no-chart-data">
            <p>No performance data yet. Data will appear after trading activity.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={history}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
              <XAxis dataKey="date" stroke="#8b92a7" />
              <YAxis stroke="#8b92a7" />
              <Tooltip 
                contentStyle={{ 
                  background: '#0f1729', 
                  border: '1px solid #1a2332', 
                  borderRadius: '8px',
                  color: '#ffffff'  // ADD THIS - white text color
                }}
                labelStyle={{ color: '#ffffff' }}  // ADD THIS - white label
                itemStyle={{ color: '#ffffff' }}   // ADD THIS - white values
                formatter={(value) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`}
              />
              <Bar dataKey="pnlPct" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

    </div>
  );
};

export default Performance;